import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { module } from 'app/modules/equipment-registration/equipment-registration.module';
import { MatDialog } from '@angular/material/dialog';
import { Mawop } from 'app/modules/equipment-registration/model/mawop.model';
import { MawopService } from 'app/modules/equipment-registration/services/mawop.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { composeUrl } from 'app/core/shared/helper/url.helper';
import { MawopCalculatorModalComponent } from 'app/modules/equipment-registration/component/mawop-calculator-modal/mawop-calculator-modal.component';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Subject, takeUntil, merge, switchMap, EMPTY, map } from 'rxjs';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';
import { EditMawopModalComponent } from './edit-mawop-modal/edit-mawop-modal.component';

@Component({
    selector: 'app-mawop',
    templateUrl: './mawop.page.html',
    standalone: true,
    imports: module,
    styleUrls: ['./mawop.page.scss'],
})
export class MawopPage {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any
    startingIndex: number = 0
    isLoading: boolean = false

    sortInfo: string;
    private columnLabels: { [key: string]: string } = {
        ...DEFAULT_COLUMN_LABELS,
        wellProductionStatus: 'Production Status',
        annAPress: 'Annulus A',
        annBPress: 'Annulus B',
        annCPress: 'Annulus C',
        annDPress: 'Annulus D',
        tubingCasingInch: 'Tubing Casing',
        productionCasingInch: 'Production Casing',
        intermediateCasingInch: 'Intermediate Casing',
        surfaceCasingInch: 'Surface Casing',
        conductorCasingInch: 'Conductor Casing',
    };

    columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
        key,
        name,
        isChecked: false, // default unchecked
    }));

    constructor(
        private dialog: MatDialog,
        private service: MawopService,
        private formBuilder: UntypedFormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort)
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page).pipe(
                switchMap(() => {
                    // this.isLoading = true;

                    let pageInfo = {
                        'count': this._paginator.length,
                        'pageSize': this._paginator.pageSize,
                        'limit': this._paginator.pageSize,
                        'offset': this._paginator.pageIndex
                    }
                    this.setPage(pageInfo); // Emit page number

                    return EMPTY
                }),
                map(() => {
                    this.isLoading = false;
                }),
            ).subscribe();
        }
    }

    setSearch() {
        this.sortInfo = 'Field'
        this.searchForm = this.formBuilder.group({
            Page: [0],
            PageSize: [20],
            OrderBy1: [null],
            OrderBy2: [null],
            OrderBy3: [null],
            OrderDir: 'asc',
            WellName: [''],
            PlatformName: [''],
            StringName: [''],
            FieldName: [''],
            SortColumns: this.formBuilder.array(
                this.columnSort.map(col => this.formBuilder.group(col))
            ) // col already has {key, name, isChecked}
        });

        this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0
        this.setPage({ offset: this.dt.page.offset });
    }

    setPage(pageInfo) {
        this.dt.page.offset = pageInfo.offset;
        this.startingIndex = this.dt.page.offset * this.dt.page.size

        this.searchForm.patchValue({
            Page: this.dt.page.offset + 1,
        });

        const params = this.searchForm.value;

        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName,
        };

        this.service
            .getMawopList({ ...params, search })
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                this.dt.page.totalRecords = pagedData.data.totalRecords;
                this.dt.rows = pagedData.data.data;
            });
    }

    downloadExcel() {
        const url = composeUrl(`/v1/Mawop/DownloadOverallMawopExcel`);
        this.service.downloadExcel(url);
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };
    }

    mawopCalculator(row: Mawop) {
        const dialogRef = this.dialog.open(MawopCalculatorModalComponent, {
            data: row, // Pass your parameters here
            width: '100%',
        });

        dialogRef.afterClosed().subscribe((result) => {
            this.setPage({ offset: this.dt.page.offset })
        });
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
    }

    onSort(sort: any) {
        if (!sort) return;
        this.searchForm.patchValue({
            OrderBy1: sort.active,
            OrderDir: sort.direction,
        });

        this.sortInfo = this.columnLabels[sort.active] || sort.active;
        this.setPage({ offset: 0 });
    }

    handleSearch(event$) {
        const field = event$?.Field;
        const platform = event$?.Platform;
        const well = event$?.Well;
        const string = event$?.String;

        if ([field, platform, well, string].some(v => v != null && v !== '')) {
            this.setPage({ offset: 0 });
        }
    }

    applySort(payload: ApplySortPayload) {
        // Patch form (fill missing with null)
        const [OrderBy1, OrderBy2, OrderBy3] = [
            payload.columns[0]?.key ?? null,
            payload.columns[1]?.key ?? null,
            payload.columns[2]?.key ?? null,
        ];

        this.searchForm.patchValue({
            OrderBy1,
            OrderBy2,
            OrderBy3,
            OrderDir: payload.orderDir ?? 'asc',
        });

        const labels = payload.columns.map(k => k.name);
        this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
        this.setPage({ offset: 0 });
    }

    editMawop(mawop) {
        const dialogRef = this.dialog.open(EditMawopModalComponent, {
            width: '50%',
            data: mawop
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.setPage({ offset: this.dt.page.offset })
            }
        });
    }
}
